/* eslint-disable prefer-template */
import { ArrowForwardIosRounded } from '@mui/icons-material';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { CustomerOverview } from '../../types/customers';
import Badge from '../global/Badge';
import { ROUTES } from '../../constants/global';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';

interface Props {
  generalRequest: CustomerOverview['generalRequests'][number];
  onSelectAppointment?: () => void;
}

function CustomerGeneralRequestRow({
  generalRequest,
  onSelectAppointment,
}: Props) {
  const theme = useTheme();
  const { user } = useUser<LoadedUser>();
  const navigate = useNavigate();

  const dateToUse = `Updated on ${format(
    new Date(generalRequest.updatedAt),
    'MM/dd/yyyy',
  )} at ${format(new Date(generalRequest.updatedAt), 'h:mma')}`;
  const artists = (
    generalRequest.selectedArtists?.length
      ? generalRequest.selectedArtists.map(employeeId =>
          employeeId === 'first-available'
            ? 'First Available'
            : user?.business?.employees?.find(
                emp => emp.employeeId === employeeId,
              )?.displayName,
        )
      : []
  ).filter(Boolean) as string[];
  const title = `Request${
    artists.length > 0 ? ` for ${artists.join(', ')}` : ''
  }`;

  const badgeProps: Pick<
    React.ComponentProps<typeof Badge>,
    'colorScheme' | 'text' | 'icon'
  > | null = {
    colorScheme: 'warning',
    text: 'Requested',
    icon: CheckIcon,
  };

  const to = `${ROUTES.requests}/${generalRequest.requestId}`;

  const Container = to ? Link : Box;

  const onLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    if (!to) return;
    e.preventDefault();
    onSelectAppointment?.();
    // run on next tick to ensure the modal is closed
    setTimeout(() => {
      navigate(to);
    }, 0);
  };

  return (
    <Container
      to={to}
      onClick={onLinkClick}
      style={{
        textDecoration: 'none',
      }}>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        gap={0.25}
        py={2}
        borderBottom={`1px solid ${theme.palette.lightGrey.main}`}>
        <Stack gap={0.25}>
          <Typography variant="h4" component="p">
            {title}
          </Typography>
          <Typography variant="body2" component="p">
            {dateToUse}
          </Typography>
          {badgeProps && (
            <Badge
              {...badgeProps}
              sx={{
                width: 'fit-content',
              }}
            />
          )}
        </Stack>
        {!!to && (
          <Box
            minHeight="100%"
            display="flex"
            alignItems="center"
            justifyContent="end">
            <ArrowForwardIosRounded
              sx={{
                fontSize: 18,
                color: 'text.disabled',
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default CustomerGeneralRequestRow;
